// The audit log: who read or changed what and when, filtered, exported, verified.
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { sv } from '@aldorock-fordon/shared/sv';
import { api } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { Table } from '../ui/Table.tsx';
import { SelectField, TextField } from '../ui/Fields.tsx';
import { moment } from '../format.ts';

type Row = {
  seq: number;
  at: string;
  actor: string;
  action: string;
  objectType: string;
  objectId: string | null;
  fields: string[];
};
type Data = { rows: Row[]; users: Array<{ id: string; name: string }> };
type SecurityEvent = { at: string; kind: string; subject: string | null };

export function AuditPage() {
  const h1 = usePage(sv.audit.title);
  const [actorId, setActorId] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [filter, setFilter] = useState('');
  const q = useQuery({
    queryKey: ['audit', filter],
    queryFn: () => api<Data>(`/admin/audit${filter}`),
  });
  const security = useQuery({
    queryKey: ['security-events'],
    queryFn: () => api<SecurityEvent[]>('/admin/security-events'),
  });
  const verify = useMutation({
    mutationFn: () => api<{ checked: number; brokenSeq: number | null }>('/admin/audit/verify'),
  });
  const params = () => {
    const p = new URLSearchParams();
    if (actorId) p.set('actorId', actorId);
    if (action) p.set('action', action);
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    const s = p.toString();
    return s ? `?${s}` : '';
  };
  return (
    <>
      <PageHead
        help="audit"
        crumbs={[{ to: '/admin', label: sv.adminHome.title }, { label: sv.audit.title }]}
        title={sv.audit.title}
        titleRef={h1}
      />
      <p>{sv.audit.intro}</p>
      <form
        className="report-form"
        onSubmit={(e) => {
          e.preventDefault();
          setFilter(params());
        }}
      >
        <div className="filter-row">
          <SelectField
            id="a-actor"
            label={sv.audit.actor}
            value={actorId}
            onChange={setActorId}
            placeholder={sv.audit.all}
            options={(q.data?.users ?? []).map((u) => ({ value: u.id, label: u.name }))}
          />
          <SelectField
            id="a-action"
            label={sv.audit.action}
            value={action}
            onChange={setAction}
            placeholder={sv.audit.all}
            options={Object.entries(sv.audit.actions).map(([value, label]) => ({ value, label }))}
          />
          <TextField
            id="a-from"
            type="date"
            label={sv.audit.from}
            value={from}
            onChange={setFrom}
          />
          <TextField id="a-to" type="date" label={sv.audit.to} value={to} onChange={setTo} />
        </div>
        <div className="form-actions">
          <button className="btn btn-primary" type="submit">
            {sv.audit.filter}
          </button>
          <a className="btn" href={`/api/v1/admin/audit/export.csv${filter}`} download>
            {sv.audit.export}
          </a>
          <button
            className="btn"
            type="button"
            onClick={() => verify.mutate()}
            disabled={verify.isPending}
          >
            {sv.audit.verify}
          </button>
        </div>
        {verify.data ? (
          <p
            className="notice"
            data-state={verify.data.brokenSeq === null ? 'done' : 'bad'}
            role="status"
          >
            {verify.data.brokenSeq === null
              ? sv.audit.verifiedOk(verify.data.checked)
              : sv.audit.verifiedBroken(verify.data.brokenSeq)}
          </p>
        ) : null}
      </form>
      <Table caption={sv.audit.rows} isEmpty={q.data?.rows.length === 0} hasOptional>
        <thead>
          <tr>
            <th scope="col">{sv.audit.when}</th>
            <th scope="col">{sv.audit.actor}</th>
            <th scope="col">{sv.audit.action}</th>
            <th scope="col">{sv.audit.object}</th>
            <th scope="col" className="col-opt">
              {sv.audit.fields}
            </th>
          </tr>
        </thead>
        <tbody>
          {(q.data?.rows ?? []).map((r) => (
            <tr key={r.seq}>
              <td className="num">{moment(r.at)}</td>
              <td>{r.actor}</td>
              <td>{sv.audit.actions[r.action] ?? r.action}</td>
              <td>{sv.audit.objects[r.objectType] ?? r.objectType}</td>
              <td className="col-opt">{r.fields.join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Table
        caption={sv.audit.security}
        isEmpty={security.data?.length === 0}
        empty={sv.audit.securityEmpty}
      >
        <thead>
          <tr>
            <th scope="col">{sv.audit.when}</th>
            <th scope="col">{sv.audit.action}</th>
            <th scope="col">{sv.audit.actor}</th>
          </tr>
        </thead>
        <tbody>
          {(security.data ?? []).map((e, i) => (
            <tr key={i}>
              <td className="num">{moment(e.at)}</td>
              <td>{sv.audit.securityKinds[e.kind] ?? e.kind}</td>
              <td>{e.subject ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}
